import { Dir, SUPPORT_RTL_LOCALES } from 'constants/vars'
import { useParams } from 'react-router-dom'
import LocalCookie, { CookieKey } from 'util/LocalCookie'

const getSignPosition = numberString => {
    // 获取正号或负号的位置
    const indexOfPlusSign = numberString.indexOf('+')
    const indexOfMinusSign = numberString.indexOf('-')

    if (indexOfPlusSign !== -1 && indexOfMinusSign !== -1) {
        // 如果同时包含正号和负号，则返回最早出现的符号位置
        return Math.min(indexOfPlusSign, indexOfMinusSign)
    } else if (indexOfPlusSign !== -1) {
        // 如果只包含正号，则返回正号的位置
        return indexOfPlusSign
    } else if (indexOfMinusSign !== -1) {
        // 如果只包含负号，则返回负号的位置
        return indexOfMinusSign
    }

    // 如果没有符号，则返回 -1
    return -1
}

interface FormatNumberStringOptions {
    direction: Dir.LTR | Dir.RTL
    input: string
    symbol?: string
    isCrypto?: boolean
    isCurrency?: boolean
    isZeroToCrossbar?: boolean
}

export const innerFormatNumberString = (props: FormatNumberStringOptions) => {
    const { direction, input, symbol, isCrypto, isCurrency, isZeroToCrossbar = false } = props

    if (
        input.trim() === '' ||
        input === 'null' ||
        input === 'undefined' ||
        input === '--' ||
        input === '-' ||
        input === 'NaN'
    ) {
        return '--'
    }
    // 传isZeroToCrossbar 为0时展示--
    if (isZeroToCrossbar && Number(input) === 0) {
        return '--'
    }

    const INCLUDE_SYMBOLS = ['%', 'X']
    const RTL_EXCLUDE_SYMBOLS = ['X']

    let sign = ''
    let numberPart = input
    let symbolPart = symbol || ''
    let formattedString = ''

    const signPosition = getSignPosition(input)
    // 提取数字部分和符号部分
    if (signPosition !== -1) {
        sign = input[signPosition]
        numberPart = input.slice(signPosition + 1)
    }
    if (direction === Dir.LTR) {
        if (isCurrency) {
            formattedString = `${symbolPart}${sign}${numberPart}`
        } else if (isCrypto) {
            formattedString = `${sign}${numberPart} ${symbolPart}`
        } else {
            if (INCLUDE_SYMBOLS.includes(symbolPart)) {
                formattedString = `${sign}${numberPart}${symbolPart}`
            } else {
                formattedString = `${sign}${numberPart} ${symbolPart}`
            }
        }
    } else {
        if (isCrypto) {
            formattedString = `${numberPart}${sign} ${symbolPart}`
        } else if (isCurrency) {
            formattedString = `${symbolPart}${numberPart}${sign}`
        } else {
            if (RTL_EXCLUDE_SYMBOLS.includes(symbolPart)) {
                formattedString = `${sign}${numberPart}${symbolPart}`
            } else {
                formattedString = `${symbolPart}${numberPart}${sign}`
            }
        }
    }

    return formattedString
}
// 合约杠杆倍数X改成x处理
export const innerFormatNumberString2 = (props: FormatNumberStringOptions) => {
    const { direction, input, symbol, isCrypto, isCurrency } = props
    if (
        input.trim() === '' ||
        input === 'null' ||
        input === 'undefined' ||
        input === '--' ||
        input === '-' ||
        input === 'NaN'
    ) {
        return '--'
    }

    const INCLUDE_SYMBOLS = ['%', 'x']
    const RTL_EXCLUDE_SYMBOLS = ['x']

    let sign = ''
    let numberPart = input
    let symbolPart = symbol || ''
    let formattedString = ''

    const signPosition = getSignPosition(input)
    // 提取数字部分和符号部分
    if (signPosition !== -1) {
        sign = input[signPosition]
        numberPart = input.slice(signPosition + 1)
    }
    if (direction === Dir.LTR) {
        if (isCurrency) {
            formattedString = `${symbolPart}${sign}${numberPart}`
        } else if (isCrypto) {
            formattedString = `${sign}${numberPart} ${symbolPart}`
        } else {
            if (INCLUDE_SYMBOLS.includes(symbolPart)) {
                formattedString = `${sign}${numberPart}${symbolPart}`
            } else {
                formattedString = `${sign}${numberPart} ${symbolPart}`
            }
        }
    } else {
        if (isCrypto) {
            formattedString = `${numberPart}${sign} ${symbolPart}`
        } else if (isCurrency) {
            formattedString = `${symbolPart}${numberPart}${sign}`
        } else {
            if (RTL_EXCLUDE_SYMBOLS.includes(symbolPart)) {
                formattedString = `${sign}${numberPart}${symbolPart}`
            } else {
                formattedString = `${symbolPart}${numberPart}${sign}`
            }
        }
    }

    return formattedString
}
/**
 * 返回当前页面的方向
 * @returns { direction: Dir.LTR |  Dir.RTL }
 */
export default function useDirection() {
    const { locales } = useParams() as { locales: string }

    const supportRtl = SUPPORT_RTL_LOCALES.includes(locales)
    // 获取当前页面的方向
    const direction = supportRtl ? Dir.RTL : Dir.LTR
    return {
        direction,
        /**
         * 格式化数字字符串（无货币单位，通常使用 % 等符号），格式上兼容法币
         * @param input 数字字符串：支持带符号的数字字符串(+、-)
         * @param symbol 符号：除 + - 之外的符号
         * @returns {string}
         */
        formatNumberString: (input: string | number, symbol?: string) => {
            return innerFormatNumberString({
                direction,
                input: String(input),
                symbol
            })
        },
        //合约杠杆倍数格式化调用 x
        formatNumberString2: (input: string | number, symbol?: string) => {
            return innerFormatNumberString2({
                direction,
                input: String(input),
                symbol
            })
        },

        // 格式化法币字符串
        formatCurrencyString: (input: string | number, symbol?: string) => {
            return innerFormatNumberString({
                direction,
                input: String(input),
                symbol,
                isCurrency: true
            })
        },

        // 格式化虚拟币字符串
        formatCryptoString: (input: string | number, symbol?: string, options = {}) => {
            return innerFormatNumberString({
                direction,
                input: String(input),
                symbol,
                isCrypto: true,
                ...options
            })
        }
    }
}

const getDirection = () => {
    const locales = LocalCookie.get(CookieKey.LANG) || 'en'
    const supportRtl = SUPPORT_RTL_LOCALES.includes(locales)
    // 获取当前页面的方向
    const direction = supportRtl ? Dir.RTL : Dir.LTR

    return direction
}

/**
 * formatNumberString() 的普通函数形式
 */
export const formatNumberStringFunc = (input: string | number, symbol?: string) => {
    return innerFormatNumberString({
        direction: getDirection(),
        input: String(input),
        symbol
    })
}

/**
 * formatCryptoStringString() 的普通函数形式
 */
export const formatCryptoStringFunc = (input: string | number, symbol?: string) => {
    return innerFormatNumberString({
        direction: getDirection(),
        input: String(input),
        symbol,
        isCrypto: true
    })
}
